/**
 * Wealth (private): types, constants, formatters and the small building blocks shared by the tabs.
 * Ported from CEO Tasks' WealthView; the server is the source of truth.
 */
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { api, ApiError } from "../../lib/api";

// ── types ────────────────────────────────────────────────
export interface Asset {
  id: string; name: string; category: string; location: string; value: number; currency: string; valuedOn: string; method: string;
  owner?: string; income?: number; kind?: string; rentalsKey?: string; rentalsIds?: string; notes?: string; createdAt?: string;
}
export interface Company { id?: string; name: string; accounts: string; notes?: string; ownValue?: number | string | null }
export interface PjEvent { id?: string; year: number; label: string; amount: number; from: string; to: string }
export interface ProjectionSettings {
  years?: number | string; surplusTo?: string; events?: PjEvent[];
  [k: string]: number | string | PjEvent[] | undefined;
}
export interface WealthSettings {
  currency?: string; dividendTaxPct: number; companies: Company[]; livingCostsYear?: number; valuationMultiple?: number;
  bands?: Record<string, [number, number]>; checklist?: Record<string, boolean>; projection?: ProjectionSettings;
  reviewMonth?: number; reviewCalendarYear?: number | null;
}
export interface Snapshot { at: string; total: number; by?: Record<string, number>; count?: number }
export interface WealthDoc { version?: number; updatedAt: string | null; settings: WealthSettings; assets: Asset[]; snapshots: Snapshot[]; reviews?: unknown[] }

export interface InflowItem {
  tier?: string; basis?: string; amount?: number; project?: string; deal?: string; description?: string; company?: string; client?: string; group?: string;
  year?: number; period?: string; date?: string; invoice?: string; bank?: string; overdue?: boolean; spreadOver?: number; stage?: string; probability?: number; value?: number;
}
export interface InflowMonth { month: string; paid?: number; contracted?: number; scheduled?: number; pipeline?: number; items?: InflowItem[] }
export interface InflowYear {
  year: number; sales: number; profit: number; cashIn: number; invoicedUnpaid?: number; notYetInvoiced?: number;
  byCompany?: Record<string, { sales: number; profit: number }>;
}
export interface ProjectRow {
  project: string; group?: string; company?: string; client?: string; year: number; period?: string;
  sale: number | null; paid: number | null; outstanding: number; status: string; overdue?: boolean; unrecordedReceipt?: number; lastPaidOn?: string;
}
export interface Inflows { months: InflowMonth[]; year?: InflowYear[]; projects?: ProjectRow[]; generatedAt?: string }

export interface PolicyRow { key: string; lo: number; hi: number; actual: number; status: "in" | "above" | "below" | "unvalued"; gap: number }
export interface LinkedRental {
  id: string; name: string; key: string; keys: string[]; monthly: number; source: "feed" | "typed"; net12: number; gross12: number; bookedNet: number;
  otherReceived12: number; otherExpected: number; otherOverdue: number; leaseLines: number; events: number; priced: number; stays: number; nights: number; complete: boolean; noStays: boolean;
}
export interface StrategyRentals {
  ok: boolean; properties: string[]; linked: LinkedRental[]; unlinked: string[]; complete?: boolean; completedMonths?: number;
  bookings12?: number; nights12?: number; bookedAhead?: number; priced12?: number; unpriced12?: number;
  other?: { leaseLines: number; events: number; received12: number; expected: number; overdue: number };
}
export interface StrategyCompany {
  id?: string; name: string; key: string | null; profits: Record<string, number>; years: number; avgProfit: number; multiple: number; multipleValue: number;
  low: number; high: number; ownValue: number | null; cash: number; value: number; basis: "own" | "multiple" | "none";
}
export interface PjRow {
  year: number; label: string; k: number; cash: number; investment: number; property: number; pension: number; companies: number; full: number; liquid: number;
  profit: number; signed: number | null; dividend: number; rents: number; living: number; surplus: number; events: Array<{ label: string; amount: number; from: string; to: string }>;
  cover: number | null; yearsCovered: number | null; breaches: string[]; shortfall: number;
  [k: string]: unknown;
}
export interface Projection {
  assumptions: ProjectionSettings & { baseProfit: number; baseProfitYears: number[]; dividendTaxPct: number; livingCostsYear: number; rentsToday: number };
  shocks: string[]; shocksAvailable: string[]; topClient: string | null; rows: PjRow[];
  headline: {
    startYear: number; endYear: number; startFull: number; endFull: number; growth: number; endLiquid: number; endCover: number | null;
    independenceYear: number | null; lowestCover: { year: number; years: number } | null; shortfallYears: number[]; breachYears: number;
  };
}
export interface Totals { total: number; by: Record<string, number>; count?: number }
export interface Strategy {
  totals: Totals; companies: StrategyCompany[]; companiesValue: number; unassigned: Record<string, number>; full: number; liquid: number; liquidPct: number;
  policy: PolicyRow[]; breaches: number;
  income: { year: number; byCategory: Record<string, number>; living: number; cover: number | null; coverWithDividend: number | null; dividendNet: number; yearsCovered: number | null; propertiesWithoutIncome: number; crm: boolean; shortLetYear: number };
  rentals: StrategyRentals;
  concentration: Array<{ key: string; label: string; share: number | null; level: string; note: string }>;
  stress: Array<{ key: string; label: string; netWorth: number; netWorthPct: number; liquidity: number; yearsAfter: number | null; note: string }>;
  succession: { duty: number; dutyPctOfLiquid: number | null; checklist: Array<{ key: string; done: boolean }>; done: number; of: number };
  assumptions: { multiple: number; dividendTaxPct: number; livingCostsYear: number };
  projection?: Projection;
}
export interface ReviewStatus { due: boolean; empty?: boolean; daysTo: number; nextDue: string; month: number; monthName: string; lastAt: string | null; lastYear: number | null; count: number; calendarYear: number | null }
export interface Review {
  status: ReviewStatus;
  baseline: { at: string; kind: "review" | "start"; total: number; full: number | null; hasItems: boolean } | null;
  now: { total: number; by: Record<string, number>; full: number; companiesValue: number; liquid: number; liquidPct: number };
  change: { total: number; totalPct: number | null; full: number | null; by: Record<string, number> } | null;
  movers: Array<{ name: string; before: number; after: number; delta: number }>;
  added: Array<{ name: string; value: number }>; removed: Array<{ name: string; value: number }>;
  notRevalued: Array<{ name: string; value: number; valuedOn: string }>; cashNotUpdated: Array<{ name: string; value: number; valuedOn: string }>;
  gaps: Array<{ key: string; text: string; where: string }>;
  companies: { thisYear: { year: number; sales: number; profit: number } | null; lastYear: { year: number; sales: number; profit: number } | null; dividendNet: number; taxPct: number };
  policy: PolicyRow[]; breaches: number;
  income: Strategy["income"];
  projection: { headline: Projection["headline"]; isPlaceholder: boolean };
  succession: Strategy["succession"];
  questions: string[];
  history: Array<{ id: string; at: string; total: number; full: number; breaches: number; cover: number | null; notes: string }>;
}

// ── constants ────────────────────────────────────────────
export const CATEGORIES = [
  { k: "cash", label: "Cash & deposits" },
  { k: "investment", label: "Investments" },
  { k: "property", label: "Property" },
  { k: "pension", label: "Pension" },
  { k: "other", label: "Other" },
];
export const METHODS = ["bank balance", "statement", "own valuation", "purchase price", "estate agent estimate", "formal valuation", "own estimate"];
export const KINDS = ["equities", "bonds", "mixed fund", "cash-like", "crypto", "other"];
export const BAND_LABELS: Record<string, string> = { property: "Property", companies: "Operating companies", investment: "Investments", cash: "Cash", pension: "Pension" };
export const CHECKLIST: Array<[string, string]> = [
  ["will", "Will up to date (property, company shares, accounts)"],
  ["shares", "Company shares: who takes over, and how they are valued"],
  ["access", "Bernice can reach every account and the key documents"],
  ["insurance", "Life and key-person cover reviewed against the duty figure"],
  ["powers", "Power of attorney / instructions if you cannot act"],
];
export const STALE_DAYS = 365;
// Password managers mistake a "Name" field for a login and draw their icon over the text
export const NO_PM = { autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true", "data-bwignore": "true", "data-form-type": "other" } as const;

// ── formatting ───────────────────────────────────────────
export const eur = (v: number) => `€${Math.round(v).toLocaleString("en-MT")}`;
export const eurShort = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `€${(v / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
  if (a >= 1e3) return `€${Math.round(v / 1e3)}k`;
  return eur(v);
};
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const daysSince = (iso: string) => Math.floor((Date.now() - new Date(`${iso}T00:00:00`).getTime()) / 86400000);
export const fmtMonth = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
export const fmtYM = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
export const fmtYMLong = (ym: string) => new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
// Full timestamps (reviews) show the LOCAL day — a review saved at 01:00 Malta is not yesterday's
export const fmtStamp = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
export const fmtDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
export const signed = (v: number) => `${v >= 0 ? "+" : "−"}${eurShort(Math.abs(v))}`;

// The rental unit(s) an asset is let as — " | " between several
export const rentalKeys = (a: Asset) => [...new Set(String(a.rentalsKey || "").split("|").map((x) => x.trim()).filter(Boolean))];
// "Infinite Fusion Technologies Ltd - Reserve Account" → IFT (same rule as the server)
export const acronym = (name: string) =>
  String(name || "").split(/\s+[-–—]\s+/)[0]!.split(/\s+/).filter((w) => w && !/^(ltd|limited|plc|co|company|inc|llc)\.?$/i.test(w)).map((w) => w[0]!.toUpperCase()).join("");
// A cash line belongs to a company when either name contains the other
export const tagMatches = (location: string, company: string) => {
  const a = String(location || "").trim().toLowerCase(), b = String(company || "").trim().toLowerCase();
  return !!a && !!b && (a.includes(b) || b.includes(a) || b.split(/[()·,-]/).some((part) => part.trim().length >= 4 && a.includes(part.trim())));
};

/** The server's own words when it sent them ({ error: "..." } in CEO Tasks' shape), else the status text. */
export const errText = (e: unknown) => {
  if (e instanceof ApiError) return e.code && /\s/.test(e.code) ? e.code : e.message || `Request failed (${e.status})`;
  return e instanceof Error ? e.message : String(e);
};

// ── queries ──────────────────────────────────────────────
export const WK = {
  access: ["wealth", "access"] as const,
  doc: ["wealth", "doc"] as const,
  strategy: ["wealth", "strategy"] as const,
  review: ["wealth", "review"] as const,
  reviewStatus: ["wealth", "review-status"] as const,
  inflows: ["wealth", "inflows"] as const,
};
export function useWealthAccess() {
  return useQuery({ queryKey: WK.access, queryFn: () => api<{ enabled: boolean }>("/api/wealth/access"), staleTime: 10 * 60_000, retry: false });
}

export function useIsPhone() {
  const q = "(max-width: 639px)";
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setPhone(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return phone;
}

// ── building blocks ──────────────────────────────────────
export function Kpi({ label, value, sub, tone, icon }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "bad" | "good"; icon?: ReactNode }) {
  return (
    <div className="panel min-w-0 p-4">
      <div className="section-label">{label}</div>
      <div className={clsx("mt-1.5 flex items-center gap-2 text-[1.375rem] font-bold leading-none tracking-tight num", tone === "bad" ? "text-red-600" : tone === "good" ? "text-emerald-600" : "text-[var(--text)]")}>
        {icon && <span className="text-brand-600">{icon}</span>}
        {value}
      </div>
      {sub && <div className="muted mt-1.5 text-xs">{sub}</div>}
    </div>
  );
}
export const KpiGrid = ({ children, className }: { children: ReactNode; className?: string }) => <div className={clsx("grid grid-cols-2 gap-3 lg:grid-cols-4", className)}>{children}</div>;

/** Label · optional bar · value. */
export function Row({ label, bar, value, muted, strong, indent }: { label: ReactNode; bar?: { pct: number; color?: string }; value: ReactNode; muted?: boolean; strong?: boolean; indent?: boolean }) {
  return (
    <div className="flex items-center gap-3 py-1 text-sm">
      <span className={clsx("min-w-0 flex-1 truncate", (muted || indent) && "muted", indent && "pl-3")}>{label}</span>
      {bar && (
        <div className="hidden h-2 flex-[1.4] overflow-hidden rounded-full bg-[var(--panel-2)] ring-1 ring-[var(--line)] sm:block">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, bar.pct))}%`, background: bar.color ?? "var(--color-brand)" }} />
        </div>
      )}
      <span className={clsx("shrink-0 text-right num", muted && "muted", strong && "font-semibold")}>{value}</span>
    </div>
  );
}

const PILL_TONE: Record<string, string> = {
  default: "bg-amber-50 text-amber-800 ring-amber-200",
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};
export function Flag({ pill, ok, children }: { pill: string; ok?: boolean; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5 text-sm">
      <span className={clsx("badge mt-0.5 shrink-0 ring-1", PILL_TONE[ok ? "ok" : "default"])}>{pill}</span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}

/** Coloured status pill (money in, projects, concentration). */
export const TONE: Record<string, string> = {
  paid: "bg-[#0b0c2a] text-white ring-[#0b0c2a]",
  contracted: "bg-brand-600 text-white ring-brand-600",
  scheduled: "bg-brand-100 text-brand-800 ring-brand-200",
  late: "bg-amber-100 text-amber-800 ring-amber-300",
  pipeline: "bg-[var(--panel-2)] text-[var(--muted)] ring-[var(--line-strong)]",
  part: "bg-sky-100 text-sky-800 ring-sky-200",
  overdue: "bg-red-100 text-red-700 ring-red-200",
  high: "bg-red-100 text-red-700 ring-red-200",
  watch: "bg-amber-100 text-amber-800 ring-amber-300",
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  unknown: "bg-[var(--panel-2)] text-[var(--muted)] ring-[var(--line-strong)]",
};
export const Pill = ({ tone, children }: { tone: string; children: ReactNode }) => <span className={clsx("badge ring-1", TONE[tone] ?? TONE.unknown)}>{children}</span>;
/** Segment colours for the stacked month bars. */
export const SEG_BG: Record<string, string> = { paid: "#0b0c2a", contracted: "#2b35ff", scheduled: "#a3a9ff", late: "#f59e0b", pipeline: "#d5d9e5" };

export function SortTh({ label, on, dir, num, onClick, style }: { label: string; on: boolean; dir: "asc" | "desc"; num?: boolean; onClick: () => void; style?: CSSProperties }) {
  return (
    <th
      className={clsx("cursor-pointer select-none hover:text-[var(--text)]", on && "!text-[var(--text)]", num && "!text-right")}
      style={style}
      onClick={onClick}
      aria-sort={on ? (dir === "asc" ? "ascending" : "descending") : "none"}
      title="Sort by this column"
    >
      {label}
      <span className="ml-1 opacity-60">{on ? (dir === "asc" ? "▲" : "▼") : "↕"}</span>
    </th>
  );
}

/** Compact input for table cells. */
export const cellInput = "input !min-h-0 !rounded-md !px-2 !py-1 text-[0.8125rem]";
